import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, useNavigate } from 'react-router-dom';
import { StyleSheetManager } from 'styled-components';
import { MotionConfig } from 'framer-motion';
import { shouldForwardMotionProp } from '../utils/styling/shouldForwardMotionProp';
import { AppContainer, AuthContainer, AuthCard, Logo } from './App.styles';

// Components
import PasswordChangeRequired from '../components/auth/PasswordChangeRequired';
import AppUpdateProvider from '../components/system/AppUpdateProvider';
import ErrorBoundary from '../components/common/ErrorBoundary';
import logo from '../assets/logo.png';

import PublicRoutes from './PublicRoutes';
import AuthenticatedRoutes from './AuthenticatedRoutes';
import useAppSession from './useAppSession';

// Context
import { AuthProvider, useAuth } from '../context/AuthContext';
import { ThemeProvider } from '../context/ThemeContext';
import { ToastProvider } from '../components/common/ToastProvider';
import { ConfirmProvider } from '../components/common/ConfirmProvider';

function AppContent() {
  const { user, loading, setUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const { sessionConfig, idleTimer, handleAutoLogout } = useAppSession({ user, setUser, navigate });

  if (loading || !sessionConfig) {
    return (
      <AppContainer>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid var(--border-color)',
            borderTop: '4px solid var(--primary-color)',
            borderRadius: 0,
            animation: 'spin 1s linear infinite'
          }}></div>
        </div>
      </AppContainer>
    );
  }

  if (!user) {
    return <PublicRoutes />;
  }

  // Check if authenticated user needs to change password
  if (user && user.password_change_required) {
    return (
      <AuthContainer>
          <AuthCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Logo>
              <img src={logo} alt="Faith Insight Logo" />
              <h1>Faith Insight</h1>
              <p>Learning Portal</p>
            </Logo>
            <PasswordChangeRequired />
        </AuthCard>
      </AuthContainer>
    );
  }

  return (
    <AuthenticatedRoutes
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      idleTimer={idleTimer}
      handleAutoLogout={handleAutoLogout}
    />
  );
}

function App() {
  // Mobile viewport height fix
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);

  // Unregister any existing service workers to prevent FetchEvent.respondWith errors
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          console.log('Unregistering service worker:', registration.scope);
          registration.unregister();
        });
      });
    }
  }, []);

  // Service worker disabled - was causing FetchEvent.respondWith errors
  // useEffect(() => {
  //   const initWorkbox = async () => {
  //     try {
  //       await workboxService.init();
  //       // Workbox initialized successfully
  //     } catch (error) {
  //       // Failed to initialize Workbox
  //     }
  //   };

  //   // Only initialize in production
  //   if (process.env.NODE_ENV === 'production') {
  //     initWorkbox();
  //   }

  //   // Listen for service worker update events
  //   const handleSWUpdate = (event) => {
  //     // Service worker update available
  //     // You can show a notification to the user here
  //   };

  //   window.addEventListener('sw-update-available', handleSWUpdate);

  //   return () => {
  //     window.removeEventListener('sw-update-available', handleSWUpdate);
  //   };
  // }, []);

  return (
    <ErrorBoundary>
      <MotionConfig reducedMotion="always">
      <StyleSheetManager shouldForwardProp={shouldForwardMotionProp}>
        <ThemeProvider>
          <ToastProvider>
            <ConfirmProvider>
              <AuthProvider>
                <AppUpdateProvider>
                  <Router basename="/portal">
                    <ErrorBoundary>
                      <AppContent />
                    </ErrorBoundary>
                  </Router>
                </AppUpdateProvider>
              </AuthProvider>
            </ConfirmProvider>
          </ToastProvider>
        </ThemeProvider>
      </StyleSheetManager>
      </MotionConfig>
    </ErrorBoundary>
  );
}

export default App;
